import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { FormatProvider, defaultLocale, type TenantLocale } from '@paige/format';
import { usePrincipal } from '../auth/AuthBoundary';

export interface TenantContextValue {
  id: string;
  name: string;
  role: 'admin' | 'manager' | 'reviewer' | 'staff';
  locale: TenantLocale;
}

const TenantContext = createContext<TenantContextValue | null>(null);

/**
 * The tenant from the URL (`/t/$tenant`) joined with the principal's membership; staff without a
 * membership see it under their staff role. Mounts the tenant's formatter for every screen below.
 */
export function TenantProvider({ tenantId, children }: { tenantId: string; children: ReactNode }) {
  const principal = usePrincipal();
  const value = useMemo<TenantContextValue>(() => {
    const m = principal.tenants.find((t) => t.id === tenantId);
    if (m) return { id: m.id, name: m.name, role: m.role, locale: m.locale };
    return { id: tenantId, name: tenantId, role: 'staff', locale: defaultLocale };
  }, [principal, tenantId]);
  return (
    <TenantContext.Provider value={value}>
      <FormatProvider locale={value.locale}>{children}</FormatProvider>
    </TenantContext.Provider>
  );
}

export function useTenant(): TenantContextValue {
  const t = useContext(TenantContext);
  if (!t) throw new Error('useTenant must be used inside <TenantProvider>');
  return t;
}
